import {
  TRANSACTION_HISTORY_API_BATCH_SIZE,
  TRANSACTION_HISTORY_PAGE_SIZE,
} from "../constants/transactionHistoryPagination";
import {
  mapTransactionHistoryUiToListParams,
  requiresClientStatusFilter,
  type TransactionHistoryUiFilters,
} from "../services/transactionListParams";
import { getTransactions } from "../services/transactionService";
import { listPayLinks } from "../services/payLinkService";
import { withoutStablecoinDust } from "./transactionAssetHelpers";
import { fetchFilledDisplayPage } from "./transactionHistoryDisplayPage";
import { mapApiTransactionsToBusinessRows } from "./businessTransactionHistoryRows";
import type { BusinessTransactionRow } from "./businessSuiteTransactionRowMap";
import { mapPaidPayLinksToBusinessTransactionRows } from "./businessPayLinkTransactionRows";

interface PaginatedData {
  results?: unknown[];
  totalPages?: number;
  totalResults?: number;
  page?: number;
  limit?: number;
}

export interface UserTransactionHistoryPageResult {
  results: unknown[];
  totalPages: number;
  totalResults: number;
  page: number;
  limit: number;
}

export type UserTransactionHistoryQuery = TransactionHistoryUiFilters;

function applyClientOnlyFilters(
  results: unknown[],
  query: TransactionHistoryUiFilters
): unknown[] {
  let filtered = results;

  if (requiresClientStatusFilter(query.filterStatus)) {
    const target = (query.filterStatus || "").trim().toLowerCase();
    filtered = filtered.filter((item) => {
      const status = String((item as { status?: string }).status || "")
        .trim()
        .toLowerCase();
      return status === target;
    });
  }

  return filtered;
}

async function fetchUserTransactionHistoryPageRaw(
  _userId: string,
  page: number,
  limit: number,
  accountId: string,
  query: UserTransactionHistoryQuery = {}
): Promise<UserTransactionHistoryPageResult> {
  const listParams = mapTransactionHistoryUiToListParams({
    ...query,
    page,
    limit,
    accountId: query.businessId ? undefined : accountId || query.accountId,
  });

  const response = await getTransactions(listParams);
  const payload = (response as { data: { data: PaginatedData } }).data.data;
  const results = applyClientOnlyFilters(
    (payload.results as unknown[]) || [],
    query
  );

  const apiTotalResults = payload.totalResults ?? results.length;
  const apiTotalPages = Math.max(
    1,
    payload.totalPages ??
      Math.ceil(apiTotalResults / Math.max(listParams.limit || limit, 1))
  );

  return {
    results,
    totalPages: apiTotalPages,
    totalResults: apiTotalResults,
    page: payload.page ?? listParams.page ?? page,
    limit: payload.limit ?? listParams.limit ?? limit,
  };
}

export async function fetchUserTransactionHistoryPage(
  userId: string,
  page: number,
  limit: number,
  accountId: string,
  query: UserTransactionHistoryQuery = {}
): Promise<UserTransactionHistoryPageResult> {
  if (limit > TRANSACTION_HISTORY_PAGE_SIZE) {
    const raw = await fetchUserTransactionHistoryPageRaw(
      userId,
      page,
      limit,
      accountId,
      query
    );
    return {
      ...raw,
      results: withoutStablecoinDust(raw.results as Record<string, unknown>[]),
    };
  }

  const filled = await fetchFilledDisplayPage({
    displayPage: page,
    displaySize: limit,
    apiBatchSize: TRANSACTION_HISTORY_API_BATCH_SIZE,
    fetchApiBatch: async (apiPage, batchLimit) => {
      const raw = await fetchUserTransactionHistoryPageRaw(
        userId,
        apiPage,
        batchLimit,
        accountId,
        query
      );
      const results = withoutStablecoinDust(
        raw.results as Record<string, unknown>[]
      );
      return {
        results,
        totalPages: raw.totalPages,
        totalResults: raw.totalResults,
      };
    },
  });

  return filled;
}

export async function fetchBusinessTransactionHistoryPage(
  userId: string,
  businessId: string,
  page: number,
  limit: number,
  accountId: string,
  query: UserTransactionHistoryQuery = {}
): Promise<UserTransactionHistoryPageResult> {
  const normalizeFilter = (value: string | undefined): string =>
    (value ?? "all").trim().toLowerCase() || "all";
  const shouldIncludePayLinks =
    normalizeFilter(query.filterType) === "all" &&
    normalizeFilter(query.filterStatus) === "all";

  let prependToFirstBatch: BusinessTransactionRow[] = [];
  if (shouldIncludePayLinks && businessId) {
    try {
      const payLinksPage = await listPayLinks(businessId, {
        page: 1,
        limit: 100,
      });
      prependToFirstBatch = mapPaidPayLinksToBusinessTransactionRows(
        payLinksPage.payLinks
      );
    } catch {
      prependToFirstBatch = [];
    }
  }

  return fetchFilledDisplayPage<BusinessTransactionRow>({
    displayPage: page,
    displaySize: limit,
    apiBatchSize: TRANSACTION_HISTORY_API_BATCH_SIZE,
    prependToFirstBatch,
    sortItems: (items) =>
      [...items].sort(
        (left, right) => (right.sortDate || 0) - (left.sortDate || 0)
      ),
    fetchApiBatch: async (apiPage, batchLimit) => {
      const raw = await fetchUserTransactionHistoryPageRaw(
        userId,
        apiPage,
        batchLimit,
        accountId,
        { ...query, businessId }
      );
      const rows = mapApiTransactionsToBusinessRows(raw.results);
      return {
        results: rows,
        totalPages: raw.totalPages,
        totalResults: raw.totalResults,
      };
    },
  });
}

export async function fetchAllUserTransactionHistory(
  userId: string,
  accountId: string,
  fetchLimit: number,
  query: UserTransactionHistoryQuery = {}
): Promise<unknown[]> {
  const firstPage = await fetchUserTransactionHistoryPageRaw(
    userId,
    1,
    fetchLimit,
    accountId,
    query
  );

  let allResults = withoutStablecoinDust(
    firstPage.results as Record<string, unknown>[]
  );

  if (firstPage.totalPages > 1) {
    const FULL_HISTORY_CONCURRENCY = 4;
    const remainingPages: number[] = [];
    for (let i = 2; i <= firstPage.totalPages; i++) {
      remainingPages.push(i);
    }

    for (
      let offset = 0;
      offset < remainingPages.length;
      offset += FULL_HISTORY_CONCURRENCY
    ) {
      const chunk = remainingPages.slice(
        offset,
        offset + FULL_HISTORY_CONCURRENCY
      );
      const responses = await Promise.all(
        chunk.map((pageNumber) =>
          fetchUserTransactionHistoryPageRaw(
            userId,
            pageNumber,
            fetchLimit,
            accountId,
            query
          )
        )
      );
      responses.forEach((response) => {
        allResults.push(
          ...withoutStablecoinDust(
            response.results as Record<string, unknown>[]
          )
        );
      });
    }
  }

  return allResults;
}
